import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import {
  formatarDataHora,
  STATUS_EVENTO_ROTULO,
  TIPO_MOVIMENTACAO_ROTULO,
  type TipoMovimentacao,
  type TipoMovimentacaoManual,
} from "@caixaflow/shared";
import { api, ErroApi, qs } from "../../../api/cliente";
import type { Evento, ItemEstoque, Movimentacao, Pagina, Produto } from "../../../api/tipos";
import { Campo } from "../../../components/Campos";
import { Carregando, FalhaAoCarregar, mensagemDeErro } from "../../../components/Estados";
import { Modal } from "../../../components/Modal";
import { FormOferta } from "../../../components/Ofertas";
import { useToast } from "../../../components/Toast";

const CLASSE_TIPO: Record<TipoMovimentacao, string> = {
  ENTRADA: "ok",
  VENDA: "",
  CANCELAMENTO: "neutro",
  AJUSTE: "alerta",
  PERDA: "perigo",
  OFERTA: "alerta",
};

export function AbaEstoque() {
  const [parametros, setParametros] = useSearchParams();
  const eventos = useQuery({ queryKey: ["eventos"], queryFn: () => api<Evento[]>("/eventos") });

  const escolhido = parametros.get("evento");
  const evento =
    eventos.data?.find((e) => e.id === escolhido) ??
    eventos.data?.find((e) => e.status === "EM_ANDAMENTO") ??
    eventos.data?.[0];

  const escolher = (id: string) => {
    const p = new URLSearchParams(parametros);
    p.set("evento", id);
    setParametros(p, { replace: true });
  };

  if (eventos.isPending) return <Carregando />;
  if (eventos.error) return <FalhaAoCarregar erro={eventos.error} tentarDeNovo={() => eventos.refetch()} />;
  if (!evento) return <div className="card vazio">Cadastre um evento para controlar o estoque.</div>;

  return (
    <>
      <div className="filtros">
        <Campo rotulo="Evento">
          <select value={evento.id} onChange={(e) => escolher(e.target.value)}>
            {eventos.data.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome} — {STATUS_EVENTO_ROTULO[e.status]}
              </option>
            ))}
          </select>
        </Campo>
      </div>
      <EstoqueDoEvento key={evento.id} evento={evento} />
    </>
  );
}

function EstoqueDoEvento({ evento }: { evento: Evento }) {
  const [lancando, setLancando] = useState<{ produtoId?: string } | null>(null);
  const [editandoCardapio, setEditandoCardapio] = useState(false);
  const [ofertando, setOfertando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const encerrado = evento.status === "ENCERRADO";

  const estoque = useQuery({
    queryKey: ["estoque", evento.id],
    queryFn: () => api<ItemEstoque[]>(`/eventos/${evento.id}/estoque`),
  });
  const movimentacoes = useQuery({
    queryKey: ["movimentacoes", evento.id, pagina],
    queryFn: () => api<Pagina<Movimentacao>>(`/eventos/${evento.id}/estoque/movimentacoes${qs({ pagina, porPagina: 30 })}`),
  });

  const noCardapio = estoque.data?.filter((i) => i.noCardapio) ?? [];
  const semSaldo = noCardapio.filter((i) => i.saldo <= 0);
  const baixos = noCardapio.filter((i) => i.saldo > 0 && i.estoqueBaixo);
  const totalPaginas = movimentacoes.data ? Math.max(1, Math.ceil(movimentacoes.data.total / movimentacoes.data.porPagina)) : 1;

  return (
    <>
      {encerrado && <div className="aviso">Evento encerrado: estoque e cardápio ficam somente para consulta.</div>}
      {evento.permiteEstoqueNegativo && !encerrado && (
        <div className="aviso alerta">Este evento permite vender sem saldo — o estoque pode ficar negativo.</div>
      )}
      {(baixos.length > 0 || semSaldo.length > 0) && (
        <div className="aviso alerta">
          {baixos.length > 0 && <div>Estoque baixo: {baixos.map((b) => `${b.nome} (${b.saldo})`).join(" · ")}</div>}
          {semSaldo.length > 0 && (
            <div>
              {semSaldo.length === noCardapio.length
                ? "Nenhum produto do cardápio tem saldo ainda — lance as entradas antes de abrir os caixas."
                : `${semSaldo.length} ${semSaldo.length === 1 ? "produto" : "produtos"} sem saldo (marcados como esgotado).`}
            </div>
          )}
        </div>
      )}

      <section>
        <div className="linha" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Saldo por produto</h2>
          {!encerrado && (
            <div className="linha-esq">
              <button className="btn fantasma" onClick={() => setEditandoCardapio(true)}>
                Editar cardápio
              </button>
              <button className="btn fantasma" onClick={() => setOfertando(true)} disabled={!estoque.data?.length}>
                Lançar oferta
              </button>
              <button className="btn primario" onClick={() => setLancando({})} disabled={!estoque.data?.length}>
                Lançar movimentação
              </button>
            </div>
          )}
        </div>
        {estoque.isPending && <Carregando />}
        {estoque.error && <FalhaAoCarregar erro={estoque.error} tentarDeNovo={() => estoque.refetch()} />}
        {estoque.data && (
          <div className="card tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Produto</th>
                  <th className="r">Saldo</th>
                  <th className="r">Entradas</th>
                  <th className="r">Vendidos</th>
                  <th className="r">Perdas</th>
                  <th className="r">Ofertas</th>
                  <th className="r">Ajustes</th>
                  <th className="r">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {estoque.data.length === 0 && (
                  <tr>
                    <td colSpan={8} className="vazio">
                      Nenhum produto no cardápio deste evento. Use “Editar cardápio”.
                    </td>
                  </tr>
                )}
                {agruparPorCategoria(estoque.data).map(([categoria, itens]) => (
                  <Fragment key={categoria}>
                    <tr className="cat">
                      <td colSpan={8}>{categoria}</td>
                    </tr>
                    {itens.map((i) => {
                      const t = i.totais;
                      return (
                        <tr key={i.produtoId} className={i.noCardapio && i.produtoAtivo ? "" : "inativo"}>
                          <td className="quebra">
                            {i.nome}{" "}
                            {!i.noCardapio && <span className="chip neutro">fora do cardápio</span>}
                            {i.noCardapio && !i.produtoAtivo && <span className="chip neutro">produto inativo</span>}
                          </td>
                          <td className="r">
                            <b>{i.saldo}</b>{" "}
                            {i.saldo <= 0 ? (
                              <span className="chip perigo">esgotado</span>
                            ) : (
                              i.estoqueBaixo && <span className="chip alerta">baixo</span>
                            )}
                          </td>
                          <td className="r">{t?.ENTRADA ?? 0}</td>
                          <td className="r">{-((t?.VENDA ?? 0) + (t?.CANCELAMENTO ?? 0))}</td>
                          <td className="r">{-(t?.PERDA ?? 0)}</td>
                          <td className="r">{-(t?.OFERTA ?? 0)}</td>
                          <td className="r">{sinal(t?.AJUSTE ?? 0)}</td>
                          <td>
                            {!encerrado && (
                              <div className="acoes">
                                <button className="link-botao" onClick={() => setLancando({ produtoId: i.produtoId })}>
                                  Lançar
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2>Movimentações</h2>
        {movimentacoes.error && <FalhaAoCarregar erro={movimentacoes.error} tentarDeNovo={() => movimentacoes.refetch()} />}
        {movimentacoes.data && (
          <div className="card tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Produto</th>
                  <th>Tipo</th>
                  <th className="r">Qtd</th>
                  <th>Motivo</th>
                  <th>Por</th>
                </tr>
              </thead>
              <tbody>
                {movimentacoes.data.itens.length === 0 && (
                  <tr>
                    <td colSpan={6} className="vazio">
                      Nenhuma movimentação ainda.
                    </td>
                  </tr>
                )}
                {movimentacoes.data.itens.map((m) => (
                  <tr key={m.id}>
                    <td className="mudo">{formatarDataHora(m.criadoEm)}</td>
                    <td className="quebra">{m.produto.nome}</td>
                    <td>
                      <span className={`chip ${CLASSE_TIPO[m.tipo]}`}>{TIPO_MOVIMENTACAO_ROTULO[m.tipo]}</span>
                    </td>
                    <td className="r">
                      <b>{sinal(m.quantidade)}</b>
                    </td>
                    <td className="quebra mudo">{m.motivo ?? "—"}</td>
                    <td>{m.usuario.nome}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPaginas > 1 && (
          <div className="linha" style={{ marginTop: 10 }}>
            <button className="btn pequeno fantasma" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
              Mais recentes
            </button>
            <span className="mudo pequeno">
              Página {pagina} de {totalPaginas}
            </span>
            <button className="btn pequeno fantasma" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>
              Anteriores
            </button>
          </div>
        )}
      </section>

      <Modal aberto={lancando !== null} titulo="Lançar movimentação" onFechar={() => setLancando(null)}>
        {lancando && estoque.data && (
          <FormMovimentacao
            evento={evento}
            itens={estoque.data}
            produtoInicial={lancando.produtoId}
            onPronto={() => setLancando(null)}
          />
        )}
      </Modal>
      <Modal aberto={ofertando} titulo="Lançar oferta" onFechar={() => setOfertando(false)}>
        {ofertando && estoque.data && (
          <FormOferta
            eventoId={evento.id}
            produtos={estoque.data
              .filter((i) => i.noCardapio && i.produtoAtivo)
              .map((i) => ({ produtoId: i.produtoId, nome: i.nome, precoCentavos: i.precoCentavos, saldo: i.saldo }))}
            onVoltar={() => setOfertando(false)}
            onPronto={() => setOfertando(false)}
          />
        )}
      </Modal>
      <Modal aberto={editandoCardapio} titulo="Cardápio do evento" onFechar={() => setEditandoCardapio(false)}>
        {editandoCardapio && estoque.data && (
          <FormCardapio evento={evento} itens={estoque.data} onPronto={() => setEditandoCardapio(false)} />
        )}
      </Modal>
    </>
  );
}

const sinal = (n: number) => (n > 0 ? `+${n}` : String(n));

function agruparPorCategoria<T extends { categoria: string }>(itens: T[]): [string, T[]][] {
  const mapa = new Map<string, T[]>();
  for (const i of itens) mapa.set(i.categoria, [...(mapa.get(i.categoria) ?? []), i]);
  return [...mapa.entries()];
}

function FormMovimentacao({
  evento,
  itens,
  produtoInicial,
  onPronto,
}: {
  evento: Evento;
  itens: ItemEstoque[];
  produtoInicial?: string;
  onPronto: () => void;
}) {
  const [produtoId, setProdutoId] = useState(produtoInicial ?? itens[0]?.produtoId ?? "");
  const [tipo, setTipo] = useState<TipoMovimentacaoManual>("ENTRADA");
  const [direcao, setDirecao] = useState<1 | -1>(1);
  const [quantidade, setQuantidade] = useState("");
  const [motivo, setMotivo] = useState("");
  const qc = useQueryClient();
  const toast = useToast();
  const item = itens.find((i) => i.produtoId === produtoId);

  const lancar = useMutation({
    mutationFn: () => {
      const q = Math.trunc(Number(quantidade));
      return api<{ saldo: number }>(`/eventos/${evento.id}/estoque/movimentacoes`, "POST", {
        produtoId,
        tipo,
        quantidade: tipo === "AJUSTE" ? q * direcao : q,
        motivo: motivo.trim() || undefined,
      });
    },
    onSuccess: (r) => {
      toast(`${TIPO_MOVIMENTACAO_ROTULO[tipo]} lançada · saldo agora ${r.saldo}`);
      qc.invalidateQueries({ queryKey: ["estoque", evento.id] });
      qc.invalidateQueries({ queryKey: ["movimentacoes", evento.id] });
      onPronto();
    },
  });
  const erro = lancar.error instanceof ErroApi ? lancar.error : null;

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    lancar.mutate();
  };

  return (
    <form className="form" onSubmit={enviar}>
      {lancar.error && !erro?.campos.length && <div className="aviso erro">{mensagemDeErro(lancar.error)}</div>}
      <Campo rotulo="Produto" dica={item ? `saldo atual ${item.saldo}` : undefined}>
        <select value={produtoId} onChange={(e) => setProdutoId(e.target.value)}>
          {itens.map((i) => (
            <option key={i.produtoId} value={i.produtoId}>
              {i.nome}
            </option>
          ))}
        </select>
      </Campo>
      <div className="abas" role="radiogroup" aria-label="Tipo" style={{ alignSelf: "flex-start" }}>
        {(["ENTRADA", "AJUSTE", "PERDA"] as const).map((t) => (
          <button key={t} type="button" role="radio" aria-selected={tipo === t} aria-checked={tipo === t} onClick={() => setTipo(t)}>
            {TIPO_MOVIMENTACAO_ROTULO[t]}
          </button>
        ))}
      </div>
      {tipo === "AJUSTE" && (
        <div className="abas" role="radiogroup" aria-label="Sentido do ajuste" style={{ alignSelf: "flex-start" }}>
          <button type="button" role="radio" aria-selected={direcao === 1} aria-checked={direcao === 1} onClick={() => setDirecao(1)}>
            + Sobrou
          </button>
          <button type="button" role="radio" aria-selected={direcao === -1} aria-checked={direcao === -1} onClick={() => setDirecao(-1)}>
            − Faltou
          </button>
        </div>
      )}
      <Campo rotulo="Quantidade" erro={erro?.doCampo("quantidade")}>
        <input
          type="number"
          min={1}
          step={1}
          inputMode="numeric"
          required
          autoFocus
          value={quantidade}
          onChange={(e) => setQuantidade(e.target.value)}
        />
      </Campo>
      <Campo rotulo="Motivo" dica={tipo === "ENTRADA" ? "opcional" : "obrigatório"} erro={erro?.doCampo("motivo")}>
        <input
          required={tipo !== "ENTRADA"}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder={tipo === "PERDA" ? "Ex.: embalagem danificada" : tipo === "AJUSTE" ? "Ex.: contagem do fim do dia" : "Ex.: reposição do fornecedor"}
        />
      </Campo>
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onPronto}>
          Cancelar
        </button>
        <button className="btn primario" type="submit" disabled={lancar.isPending}>
          {lancar.isPending ? "Lançando…" : "Lançar"}
        </button>
      </div>
    </form>
  );
}

function FormCardapio({ evento, itens, onPronto }: { evento: Evento; itens: ItemEstoque[]; onPronto: () => void }) {
  const produtos = useQuery({ queryKey: ["produtos", { verArquivados: false }], queryFn: () => api<Produto[]>("/produtos") });
  const [marcados, setMarcados] = useState(() => new Set(itens.filter((i) => i.noCardapio).map((i) => i.produtoId)));
  const qc = useQueryClient();
  const toast = useToast();

  const salvar = useMutation({
    mutationFn: () => api(`/eventos/${evento.id}/cardapio`, "PUT", { produtoIds: [...marcados] }),
    onSuccess: () => {
      toast("Cardápio atualizado");
      qc.invalidateQueries({ queryKey: ["estoque", evento.id] });
      onPronto();
    },
  });

  const alternar = (id: string) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });

  if (produtos.isPending) return <Carregando />;
  if (produtos.error) return <FalhaAoCarregar erro={produtos.error} />;

  const grupos = agruparPorCategoria(produtos.data.map((p) => ({ ...p, categoria: p.categoria.nome })));
  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        salvar.mutate();
      }}
    >
      <p className="mudo pequeno" style={{ margin: 0 }}>
        Marque o que será vendido em <b>{evento.nome}</b>. Tirar um produto do cardápio não apaga o histórico de estoque.
      </p>
      {salvar.error && <div className="aviso erro">{mensagemDeErro(salvar.error)}</div>}
      {grupos.map(([categoria, lista]) => (
        <fieldset key={categoria} className="form" style={{ border: 0, padding: 0, margin: 0, gap: 10 }}>
          <legend>
            <h2 style={{ margin: "0 0 6px" }}>{categoria}</h2>
          </legend>
          {lista.map((p) => (
            <label key={p.id} className="marcar">
              <input type="checkbox" checked={marcados.has(p.id)} onChange={() => alternar(p.id)} />
              <span>
                {p.nome} {!p.ativo && <span className="chip neutro">inativo</span>}
              </span>
            </label>
          ))}
        </fieldset>
      ))}
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onPronto}>
          Cancelar
        </button>
        <button className="btn primario" type="submit" disabled={salvar.isPending}>
          {salvar.isPending ? "Salvando…" : `Salvar (${marcados.size})`}
        </button>
      </div>
    </form>
  );
}
